// BoatShowSchedulePage.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  IconButton,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Fab,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CardActions,
  Grid, // Import Grid from @mui/material
  InputAdornment, // Import InputAdornment for search icon
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  NavigateNext,
  Close,
  DirectionsBoat,
  DateRange,
  LocationOn,
  Schedule,
  Event,
  AccessTime,
  Group,
  Visibility,
  Search, // Import Search icon
} from "@mui/icons-material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";

// Types
interface BoatShow {
  id: string;
  name: string;
  location: string;
  startDate: Date;
  endDate: Date;
  description?: string;
}

interface BoatShowSchedule {
  id: string;
  boatShowId: string;
  startDate: Date;
  endDate: Date;
  defaultMaxAppointments: number;
  location: string;
  notes: string;
  frequency: string;
  startTime: string;
  nbrTest: number;
  boatShow?: BoatShow;
  dailyAppointmentLimits?: Record<string, number>;
  modelId?: number;
  model?: {
    modele: string;
  };
}

interface DailyLimit {
  date: string;
  maxAppointments: number;
}

// API base URL
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const BoatShowSchedulePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // States for data
  const [schedules, setSchedules] = useState<BoatShowSchedule[]>([]);
  const [boatShows, setBoatShows] = useState<BoatShow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // States for modals
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);
  const [models, setModels] = useState<Array<{ id: number; modele: string }>>(
    []
  );
  const [isViewLimitsDialogOpen, setIsViewLimitsDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] =
    useState<BoatShowSchedule | null>(null);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // State for new schedule
  const [newSchedule, setNewSchedule] = useState<Partial<BoatShowSchedule>>({
    boatShowId: "",
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // +7 days
    defaultMaxAppointments: 10,
    location: "",
    notes: "",
    frequency: "1h",
    startTime: "09:00",
    nbrTest: 10,
    modelId: undefined,
  });

  // State for daily limits
  const [dailyLimits, setDailyLimits] = useState<DailyLimit[]>([]);
  const [newDailyLimit, setNewDailyLimit] = useState<DailyLimit>({
    date: new Date().toISOString().split("T")[0],
    maxAppointments: 10,
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Filter states
  const [modelFilter, setModelFilter] = useState<string>("");
  const [boatShowFilter, setBoatShowFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const durations = Array.from({ length: 21 }, (_, i) => 15 + i * 15);

  const getUserRole = (): string | null => {
    return localStorage.getItem("role");
  };

  const isAgent = getUserRole() === "agent";

  // Load data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch boat shows
      const boatShowsResponse = await axios.get(`${API_BASE_URL}/boatshows`);
      setBoatShows(boatShowsResponse.data);
      // Fetch schedules
      const schedulesResponse = await axios.get(
        `${API_BASE_URL}/boatshow-schedules`
      );
      setSchedules(schedulesResponse.data);
      console.log(schedulesResponse.data);
      const modelsResponse = await axios.get(`${API_BASE_URL}/modeles`);
      console.log(modelsResponse.data);
      setModels(modelsResponse.data);
      setLoading(false);
    } catch (err) {
      setError("Error loading data");
      setLoading(false);
      console.error("Error fetching data:", err);
    }
  };

  // Create a schedule
  const handleCreateSchedule = async () => {
    try {
      if (
        !newSchedule.boatShowId ||
        !newSchedule.startDate ||
        !newSchedule.endDate ||
        newSchedule.nbrTest === undefined
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      // Prepare data to send
      const scheduleToCreate = {
        ...newSchedule,
        modelId: newSchedule.modelId,
      };

      const response = await axios.post(
        `${API_BASE_URL}/boatshow-schedules`,
        scheduleToCreate
      );
      setSchedules((prev) => [...prev, response.data]);
      toast.success("Schedule created successfully");
      setIsDialogOpen(false);
      setNewSchedule({
        boatShowId: "",
        startDate: new Date(),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        defaultMaxAppointments: 10,
        location: "",
        notes: "",
        frequency: "1h",
        startTime: "09:00",
        nbrTest: 10,
        modelId: undefined,
      });
      fetchData();
    } catch (err) {
      toast.error("Error creating schedule");
      console.error("Error creating schedule:", err);
    }
  };

  // Update a schedule
  const handleUpdateSchedule = async () => {
    try {
      if (!selectedSchedule) return;

      // Prepare data to send
      const scheduleToUpdate = {
        ...newSchedule,
        modelId: newSchedule.modelId,
      };

      const response = await axios.put(
        `${API_BASE_URL}/boatshow-schedules/${selectedSchedule.id}`,
        scheduleToUpdate
      );
      setSchedules((prev) =>
        prev.map((schedule) =>
          schedule.id === selectedSchedule.id ? response.data : schedule
        )
      );
      toast.success("Schedule updated successfully");
      setIsDialogOpen(false);
      setSelectedSchedule(null);
      fetchData();
    } catch (err) {
      toast.error("Error updating schedule");
      console.error("Error updating schedule:", err);
    }
  };

  // Delete a schedule
  const handleDeleteSchedule = async () => {
    try {
      if (!selectedSchedule) return;
      await axios.delete(
        `${API_BASE_URL}/boatshow-schedules/${selectedSchedule.id}`
      );
      setSchedules((prev) =>
        prev.filter((schedule) => schedule.id !== selectedSchedule.id)
      );
      toast.success("Schedule deleted successfully");
      setIsDeleteDialogOpen(false);
      setSelectedSchedule(null);
    } catch (err) {
      toast.error("Error deleting schedule");
      console.error("Error deleting schedule:", err);
    }
  };

  // Add a daily limit
  const handleAddDailyLimit = () => {
    if (newDailyLimit.date && newDailyLimit.maxAppointments > 0) {
      setDailyLimits((prev) => [...prev, { ...newDailyLimit }]);
      setNewDailyLimit({
        date: new Date().toISOString().split("T")[0],
        maxAppointments: 10,
      });
    }
  };

  // Remove a daily limit
  const handleRemoveDailyLimit = (index: number) => {
    setDailyLimits((prev) => prev.filter((_, i) => i !== index));
  };

  // Save all daily limits
  const handleSaveDailyLimits = async () => {
    try {
      if (!selectedSchedule) return;
      const response = await axios.post(
        `${API_BASE_URL}/boatshow-schedules/${selectedSchedule.id}/daily-limit`,
        {
          limits: dailyLimits,
        }
      );
      setSchedules((prev) =>
        prev.map((schedule) =>
          schedule.id === selectedSchedule.id ? response.data : schedule
        )
      );
      toast.success("Daily limits saved successfully");
      setIsLimitDialogOpen(false);
      setDailyLimits([]);
      fetchData();
    } catch (err) {
      toast.error("Error saving daily limits:");
    }
  };

  // Open edit modal
  const handleEditSchedule = (schedule: BoatShowSchedule) => {
    setSelectedSchedule(schedule);
    const modelId =
      schedule.modelId ??
      (schedule.model
        ? models.find((m) => m.modele === schedule.model?.modele)?.id
        : undefined);

    setNewSchedule({
      boatShowId: schedule.boatShowId,
      startDate: schedule.startDate,
      endDate: schedule.endDate,
      defaultMaxAppointments: schedule.defaultMaxAppointments,
      location: schedule.location,
      notes: schedule.notes,
      frequency: schedule.frequency,
      startTime: schedule.startTime,
      nbrTest: schedule.nbrTest,
      modelId: modelId,
    });
    setIsDialogOpen(true);
  };

  // Open limits modal
  const handleOpenLimitDialog = (schedule: BoatShowSchedule) => {
    setSelectedSchedule(schedule);
    setDailyLimits([]);
    setIsLimitDialogOpen(true);
  };

  // Open view limits modal
  const handleOpenViewLimitsDialog = (schedule: BoatShowSchedule) => {
    setSelectedSchedule(schedule);
    setIsViewLimitsDialogOpen(true);
  };

  // Close modals
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedSchedule(null);
    setNewSchedule({
      boatShowId: "",
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      defaultMaxAppointments: 10,
      location: "",
      notes: "",
      frequency: "1h",
      startTime: "09:00",
      nbrTest: 10,
      modelId: undefined,
    });
  };

  const handleCloseLimitDialog = () => {
    setIsLimitDialogOpen(false);
    setSelectedSchedule(null);
    setDailyLimits([]);
  };

  const handleCloseViewLimitsDialog = () => {
    setIsViewLimitsDialogOpen(false);
    setSelectedSchedule(null);
  };

  // Calculate number of days between two dates
  const calculateDuration = (start: Date, end: Date): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  };

  // Pagination handling
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter schedules based on filters and search term
  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => {
      const matchesModel = modelFilter
        ? schedule.model?.modele === modelFilter
        : true;
      const matchesBoatShow = boatShowFilter
        ? schedule.boatShowId === boatShowFilter
        : true;
      const matchesSearch = searchTerm
        ? schedule.boatShow?.name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          schedule.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
          schedule.model?.modele
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        : true;

      return matchesModel && matchesBoatShow && matchesSearch;
    });
  }, [schedules, modelFilter, boatShowFilter, searchTerm]);

  // Calculate visible schedules
  const visibleSchedules = filteredSchedules.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const dialogDuration = useMemo(() => {
    if (newSchedule.startDate && newSchedule.endDate) {
      return calculateDuration(newSchedule.startDate, newSchedule.endDate);
    }
    return 0;
  }, [newSchedule.startDate, newSchedule.endDate]);

  if (loading) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button onClick={fetchData} variant="contained">
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
        <Link color="inherit" href="#" underline="hover">
          HOME
        </Link>
        <Typography color="text.primary">SCHEDULE MANAGEMENT</Typography>
      </Breadcrumbs>
      <Box mb={6} textAlign="center">
        <Typography
          variant="overline"
          sx={{ 
            color: '#C4B08C', 
            fontWeight: 800, 
            letterSpacing: 2,
            fontSize: '0.9rem' 
          }}
        >
          MANAGEMENT
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: '#1a1a1a',
            mb: 1,
            fontFamily: 'Playfair Display, serif',
          }}
        >
          Boat Show Schedules
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}
        >
          Manage your exhibition timelines and appointments efficiently.
        </Typography>

        {!isAgent && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setIsDialogOpen(true)}
            sx={{
              bgcolor: "#C4B08C",
              color: "#fff",
              "&:hover": { bgcolor: "#b39d77", transform: 'translateY(-2px)' },
              px: 4,
              py: 1.5,
              fontWeight: 600,
              borderRadius: '50px',
              boxShadow: "0 10px 20px rgba(196, 176, 140, 0.3)",
              transition: 'all 0.3s ease',
            }}
          >
            Create New Schedule
          </Button>
        )}
      </Box>

      {/* Filters Section */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.05)',
          bgcolor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
          Filters & Search
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 250px' }}>
            <TextField
              fullWidth
              placeholder="Search..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: 3, bgcolor: '#f9f9f9', '& fieldset': { border: 'none' } }
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px' }}>
            <FormControl fullWidth>
              <InputLabel>Model</InputLabel>
              <Select
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value as string)}
                label="Model"
                sx={{ borderRadius: 3 }}
              >
                <MenuItem value="">All Models</MenuItem>
                {models.map((model) => (
                  <MenuItem key={model.id} value={model.modele}>
                    {model.modele}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '1 1 200px' }}>
            <FormControl fullWidth>
              <InputLabel>Boat Show</InputLabel>
              <Select
                value={boatShowFilter}
                onChange={(e) => setBoatShowFilter(e.target.value as string)}
                label="Boat Show"
                sx={{ borderRadius: 3 }}
              >
                <MenuItem value="">All Boat Shows</MenuItem>
                {boatShows.map((show) => (
                  <MenuItem key={show.id} value={show.id}>
                    {show.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Schedules Table / Mobile View */}
      {/* Schedules Table / Mobile View */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          mb: 4,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.05)'
        }}
      >
        <Typography variant="h6" fontWeight={600} mb={3}>
          Schedule List
        </Typography>
        {isMobile ? (
          <Box display="flex" flexDirection="column" gap={2}>
            {visibleSchedules.map((schedule) => (
              <Box key={schedule.id}>
                <Card sx={{ border: "1px solid #C4B08C", borderRadius: 2 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <DirectionsBoat fontSize="small" color="primary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {schedule.boatShow?.name || "Unknown Show"}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {schedule.location}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <DateRange fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(schedule.startDate).toLocaleDateString()} -{" "}
                        {new Date(schedule.endDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body2" fontWeight={600}>
                        Duration:
                      </Typography>
                      <Typography variant="body2">
                        {calculateDuration(
                          schedule.startDate,
                          schedule.endDate
                        )}{" "}
                        days
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="body2" fontWeight={600}>
                        Nbr Test:
                      </Typography>
                      <Typography variant="body2">
                        {schedule.nbrTest}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={600}>
                        Default Limit:
                      </Typography>
                      <Chip
                        label={`${schedule.defaultMaxAppointments} appts/day`}
                        size="small"
                        sx={{ bgcolor: "#C4B08C", color: "white" }}
                      />
                    </Box>
                  </CardContent>
                  <CardActions
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 16px",
                      borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<Visibility />}
                      href={`/boatshow-Appointment?idshow=${schedule.boatShow?.id}&idboatshow-schedules=${schedule.id}`}
                      size="small"
                      sx={{
                        bgcolor: "#C4B08C",
                        color: "white",
                        "&:hover": { bgcolor: "#b39d77" },
                        minWidth: 0,
                        flex: 1,
                        mr: 1,
                      }}
                    >
                      View
                    </Button>
                    <Tooltip title={isAgent ? "Access denied" : "Edit"}>
                      <span>
                        <IconButton
                          onClick={() => handleEditSchedule(schedule)}
                          size="small"
                          disabled={isAgent}
                          sx={{
                            color: "#1976d2",
                            "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={isAgent ? "Access denied" : "Delete"}>
                      <span>
                        <IconButton
                          onClick={() => {
                            setSelectedSchedule(schedule);
                            setIsDeleteDialogOpen(true);
                          }}
                          size="small"
                          disabled={isAgent}
                          sx={{
                            color: "#d32f2f",
                            "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 700, py: 2, color: '#555' }}>Boat Show</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 2, color: '#555' }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 2, color: '#555' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 2, color: '#555' }}>Model</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 2, color: '#555' }}>Default Limit</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 2, color: '#555' }}>Tests</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 2, color: '#555' }}>
                    Days
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 2, color: '#555' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleSchedules.map((schedule) => (
                  <TableRow key={schedule.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>
                        {schedule.boatShow?.name || "Unknown Show"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {schedule.boatShow?.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(schedule.startDate).toLocaleDateString()} -{" "}
                      {new Date(schedule.endDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{schedule.location}</TableCell>
                    <TableCell>{schedule.model?.modele}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${schedule.defaultMaxAppointments} appts/day`}
                        size="small"
                        sx={{ bgcolor: "#C4B08C", color: "white" }}
                      />
                    </TableCell>
                    <TableCell>{schedule.nbrTest}</TableCell>
                    <TableCell>
                      {calculateDuration(schedule.startDate, schedule.endDate)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Appointments">
                          <IconButton
                            size="small"
                            href={`/boatshow-Appointment?idshow=${schedule.boatShow?.id}&idboatshow-schedules=${schedule.id}&model=${schedule.model?.modele}`}
                            sx={{ color: "#C4B08C" }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isAgent ? "Access denied" : "Edit"}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditSchedule(schedule)}
                            sx={{
                              color: "#1976d2",
                              "&.Mui-disabled": {
                                color: "rgba(0, 0, 0, 0.26)",
                              },
                            }}
                            disabled={isAgent}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isAgent ? "Access denied" : "Delete"}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedSchedule(schedule);
                              setIsDeleteDialogOpen(true);
                            }}
                            sx={{ color: "#d32f2f" }}
                            disabled={isAgent}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredSchedules.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
        />
      </Paper>

      {/* Statistics */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        gap={3}
        mb={4}
      >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            boxShadow: "0 10px 30px rgba(196, 176, 140, 0.2)",
            flex: 1,
            background: "linear-gradient(135deg, #C4B08C 0%, #aa926d 100%)",
            color: "white",
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box position="relative" zIndex={1}>
            <Typography variant="h6" fontWeight={600} mb={1} sx={{ opacity: 0.9 }}>
              Total Schedules
            </Typography>
            <Typography variant="h3" fontWeight={700}>
              {filteredSchedules.length}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>Active schedules</Typography>
          </Box>
          <Schedule sx={{ position: 'absolute', right: -20, bottom: -20, fontSize: 150, opacity: 0.2 }} />
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            boxShadow: "0 10px 30px rgba(44, 44, 44, 0.2)",
            flex: 1,
            background: "linear-gradient(135deg, #2c2c2c 0%, #000000 100%)",
            color: "white",
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box position="relative" zIndex={1}>
            <Typography variant="h6" fontWeight={600} mb={1} sx={{ opacity: 0.9 }}>
              Total Boat Shows
            </Typography>
            <Typography variant="h3" fontWeight={700}>
              {boatShows.length}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Shows available
            </Typography>
          </Box>
           <DirectionsBoat sx={{ position: 'absolute', right: -20, bottom: -20, fontSize: 150, opacity: 0.2 }} />
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            boxShadow: "0 10px 30px rgba(28, 64, 88, 0.2)",
            flex: 1,
            background: "linear-gradient(135deg, #1c4058 0%, #0f2431 100%)", // Deep navy
            color: "white",
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box position="relative" zIndex={1}>
            <Typography variant="h6" fontWeight={600} mb={1} sx={{ opacity: 0.9 }}>
              Unique Shows
            </Typography>
            <Typography variant="h3" fontWeight={700}>
              {new Set(filteredSchedules.map((s) => s.boatShowId)).size}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>With schedules</Typography>
          </Box>
           <LocationOn sx={{ position: 'absolute', right: -20, bottom: -20, fontSize: 150, opacity: 0.2 }} />
        </Paper>
      </Box>

      {/* Create/Edit Modal */}
      <Dialog
        open={isDialogOpen}
        onClose={(event, reason) => {
          // Only close when the X button is clicked
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseDialog();
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8f8f8",
            borderBottom: "1px solid #eaeaea",
          }}
        >
          <Typography variant="h6" fontWeight="600">
            {selectedSchedule ? "Edit Schedule" : "Create Schedule"}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="boat-show-label">Boat Show *</InputLabel>
            <Select
              labelId="boat-show-label"
              value={newSchedule.boatShowId}
              onChange={(e) =>
                setNewSchedule({ ...newSchedule, boatShowId: e.target.value })
              }
              label="Boat Show *"
            >
              {boatShows.map((show) => (
                <MenuItem key={show.id} value={show.id}>
                  {show.name} - {show.location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            sx={{ mb: 2 }}
          >
            <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
              <TextField
                fullWidth
                type="date"
                label="Start Date *"
                InputLabelProps={{ shrink: true }}
                value={
                  new Date(newSchedule.startDate || new Date())
                    .toISOString()
                    .split("T")[0]
                }
                onChange={(e) =>
                  setNewSchedule({
                    ...newSchedule,
                    startDate: new Date(e.target.value),
                  })
                }
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
              <TextField
                fullWidth
                type="date"
                label="End Date *"
                InputLabelProps={{ shrink: true }}
                value={
                  new Date(newSchedule.endDate || new Date())
                    .toISOString()
                    .split("T")[0]
                }
                onChange={(e) =>
                  setNewSchedule({
                    ...newSchedule,
                    endDate: new Date(e.target.value),
                  })
                }
              />
            </Box>
          </Box>
          <TextField
            fullWidth
            label="Duration (Days)"
            value={dialogDuration}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="modele-label">Modèle</InputLabel>
              <Select
                labelId="modele-label"
                value={newSchedule.modelId ?? ""}
                label="Modèle"
                onChange={(e) =>
                  setNewSchedule({
                    ...newSchedule,
                    modelId: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
              >
                <MenuItem value="">None</MenuItem>
                {models.map((modele) => (
                  <MenuItem key={modele.id} value={modele.id}>
                    {modele.modele}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            label="Location at the show"
            value={newSchedule.location || ""}
            onChange={(e) =>
              setNewSchedule({ ...newSchedule, location: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Default appointment limit"
            value={newSchedule.defaultMaxAppointments || 10}
            onChange={(e) =>
              setNewSchedule({
                ...newSchedule,
                defaultMaxAppointments: parseInt(e.target.value),
              })
            }
            inputProps={{ min: 1, max: 100 }}
            sx={{ mb: 2 }}
          />
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            sx={{ mb: 2 }}
          >
            <Box sx={{ width: { xs: "100%", sm: "33.3%" } }}>
              <FormControl fullWidth>
                <InputLabel id="frequency-label">Frequency</InputLabel>
                <Select
                  labelId="frequency-label"
                  value={newSchedule.frequency || "30"}
                  label="Frequency"
                  onChange={(e) =>
                    setNewSchedule({
                      ...newSchedule,
                      frequency: e.target.value.toString(),
                    })
                  }
                >
                  {durations.map((minutes) => (
                    <MenuItem key={minutes} value={minutes}>
                      {minutes < 60
                        ? `${minutes} min`
                        : `${Math.floor(minutes / 60)}h ${
                            minutes % 60 !== 0 ? `${minutes % 60} min` : ""
                          }`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "33.3%" } }}>
              <TextField
                fullWidth
                type="time"
                label="Start Time"
                InputLabelProps={{ shrink: true }}
                value={newSchedule.startTime || ""}
                onChange={(e) =>
                  setNewSchedule({ ...newSchedule, startTime: e.target.value })
                }
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "33.3%" } }}>
              <TextField
                fullWidth
                type="number"
                label="Number of Tests"
                value={newSchedule.nbrTest || 0}
                onChange={(e) =>
                  setNewSchedule({
                    ...newSchedule,
                    nbrTest: parseInt(e.target.value),
                  })
                }
                inputProps={{ min: 1 }}
              />
            </Box>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={newSchedule.notes || ""}
            onChange={(e) =>
              setNewSchedule({ ...newSchedule, notes: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #eaeaea" }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={
              selectedSchedule ? handleUpdateSchedule : handleCreateSchedule
            }
            sx={{
              bgcolor: "#C4B08C",
              color: "#000",
              "&:hover": { bgcolor: "#b39d77" },
              px: 3,
            }}
          >
            {selectedSchedule ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily Limits Modal */}
      <Dialog
        open={isLimitDialogOpen}
        onClose={(event, reason) => {
          // Only close when the X button is clicked
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseLimitDialog();
          }
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8f8f8",
            borderBottom: "1px solid #eaeaea",
          }}
        >
          <Typography variant="h6" fontWeight="600">
            Set Daily Appointment Limits
          </Typography>
          <IconButton onClick={handleCloseLimitDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedSchedule && (
            <>
              <Typography variant="body1" fontWeight={500} mb={2}>
                For:{" "}
                <span style={{ color: "#C4B08C" }}>
                  {selectedSchedule.boatShow?.name}
                </span>
              </Typography>
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={2}
                alignItems="center"
                mb={2}
              >
                <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date"
                    InputLabelProps={{ shrink: true }}
                    value={newDailyLimit.date}
                    onChange={(e) =>
                      setNewDailyLimit({
                        ...newDailyLimit,
                        date: e.target.value,
                      })
                    }
                    inputProps={{
                      min: new Date(selectedSchedule.startDate)
                        .toISOString()
                        .split("T")[0],
                      max: new Date(selectedSchedule.endDate)
                        .toISOString()
                        .split("T")[0],
                    }}
                  />
                </Box>
                <Box sx={{ width: { xs: "100%", sm: "40%" } }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Appointments"
                    value={newDailyLimit.maxAppointments}
                    onChange={(e) =>
                      setNewDailyLimit({
                        ...newDailyLimit,
                        maxAppointments: parseInt(e.target.value),
                      })
                    }
                    inputProps={{ min: 1, max: 100 }}
                  />
                </Box>
                <Box sx={{ width: { xs: "100%", sm: "30%" } }}>
                  <Button
                    variant="contained"
                    onClick={handleAddDailyLimit}
                    fullWidth
                    sx={{
                      bgcolor: "#C4B08C",
                      color: "#000",
                      "&:hover": { bgcolor: "#b39d77" },
                      height: "56px",
                    }}
                  >
                    Add Limit
                  </Button>
                </Box>
              </Box>
              {dailyLimits.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Limits to be added:
                  </Typography>
                  <List dense>
                    {dailyLimits.map((limit, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={new Date(limit.date).toLocaleDateString()}
                          secondary={`Max appointments: ${limit.maxAppointments}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveDailyLimit(index)}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              <Typography variant="body2" color="text.secondary" mt={2}>
                Default limit: {selectedSchedule.defaultMaxAppointments}{" "}
                appointments/day
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #eaeaea" }}>
          <Button onClick={handleCloseLimitDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDailyLimits}
            disabled={dailyLimits.length === 0}
            sx={{
              bgcolor: "#C4B08C",
              color: "#000",
              "&:hover": { bgcolor: "#b39d77" },
              px: 3,
            }}
          >
            Save Limits
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Limits Modal */}
      <Dialog
        open={isViewLimitsDialogOpen}
        onClose={(event, reason) => {
          // Only close when the X button is clicked
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseViewLimitsDialog();
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8f8f8",
            borderBottom: "1px solid #eaeaea",
          }}
        >
          <Typography variant="h6" fontWeight="600">
            Daily Appointment Limits
          </Typography>
          <IconButton onClick={handleCloseViewLimitsDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedSchedule && (
            <>
              <Typography variant="body1" fontWeight={500} mb={2}>
                For:{" "}
                <span style={{ color: "#C4B08C" }}>
                  {selectedSchedule.boatShow?.name}
                </span>
              </Typography>
              {Object.keys(selectedSchedule.dailyAppointmentLimits || {})
                .length > 0 ? (
                <List>
                  {selectedSchedule.dailyAppointmentLimits &&
                    Object.entries(selectedSchedule.dailyAppointmentLimits).map(
                      ([date, limit]) => (
                        <React.Fragment key={date}>
                          <ListItem>
                            <ListItemText
                              primary={new Date(date).toLocaleDateString()}
                              secondary={`Max appointments: ${limit}`}
                            />
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      )
                    )}
                </List>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  py={3}
                >
                  No specific daily limits set
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" mt={2}>
                Default limit: {selectedSchedule.defaultMaxAppointments}{" "}
                appointments/day
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseViewLimitsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={(event, reason) => {
          // Only close when the X button is clicked
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setIsDeleteDialogOpen(false);
          }
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the schedule for "
            {selectedSchedule?.boatShow?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteSchedule}
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating button for mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => setIsDialogOpen(true)}
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            bgcolor: "#C4B08C",
            "&:hover": { bgcolor: "#b39d77" },
          }}
        >
          <Add />
        </Fab>
      )}
    </Container>
  );
};

export default BoatShowSchedulePage;
